// React and hooks
import React, { useEffect, useRef, useState } from 'react';

// Icons from lucide-react
import {
  Boxes,
  Weight,
  Tag,
  Factory,
  ClipboardList,
  Package,
  ChevronDown,
  ChevronRight,
  Search,
  LayoutDashboard,
  ChevronLeft,
  ChevronRight as ChevronRightIcon,
} from 'lucide-react';

// Motion for animations
import { motion } from 'framer-motion';

// Components
import InventoryStockLed from '../components/InventoryStockLed';
import PageHeading from '../components/PageHeading';

// API client
import api from '../lib/api';

// i18n
import { useTranslation } from 'react-i18next';

const DASHBOARD_LOCATIONS = [
  { key: 'coldstorage1', labelKey: 'coldStorage1' },
  { key: 'coldstorage2', labelKey: 'coldStorage2' },
  { key: 'coldstorage3', labelKey: 'coldStorage3' },
  { key: 'refrigerators', labelKey: 'refrigerators' },
  { key: 'totems', labelKey: 'totems' },
  { key: 'warehouse', labelKey: 'warehouse' },
];

const Dashboard = () => {
  // States for brands, inventory data and collapsible sections
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [data, setData] = useState({});
  const [openSections, setOpenSections] = useState({});
  const [sortOption, setSortOption] = useState('boxesDesc');
  const [searchQuery, setSearchQuery] = useState('');
  const [locationFilter, setLocationFilter] = useState('all');
  const [movementDate, setMovementDate] = useState(() => {
    const today = new Date();
    const localDate = new Date(today.getTime() - today.getTimezoneOffset() * 60000);
    return localDate.toISOString().slice(0, 10);
  });
  const [dailyEntries, setDailyEntries] = useState([]);
  const [dailyExits, setDailyExits] = useState([]);
  const [movementsLoading, setMovementsLoading] = useState(false);
  const entriesScrollRef = useRef(null);
  const exitsScrollRef = useRef(null);
  const [loading, setLoading] = useState(true); // loading state
  const { t } = useTranslation();

  useEffect(() => {
    let active = true;
    const loadDailyMovements = async () => {
      setMovementsLoading(true);
      try {
        const locations = locationFilter === 'all'
          ? DASHBOARD_LOCATIONS.map(({ key }) => key)
          : [locationFilter];
        const results = await Promise.all(locations.flatMap((location) => [
          api.get(`/inventory/entries?location=${location}&date=${movementDate}`),
          api.get(`/inventory/exits?location=${location}&date=${movementDate}`),
        ]));
        if (!active) return;
        setDailyEntries(results.filter((_, index) => index % 2 === 0).flatMap((result) => Array.isArray(result.data) ? result.data : []));
        setDailyExits(results.filter((_, index) => index % 2 === 1).flatMap((result) => Array.isArray(result.data) ? result.data : []));
      } catch (err) {
        console.error('Failed to load daily inventory movements', err);
        if (active) {
          setDailyEntries([]);
          setDailyExits([]);
        }
      } finally {
        if (active) setMovementsLoading(false);
      }
    };
    loadDailyMovements();
    return () => { active = false; };
  }, [movementDate, locationFilter]);

  const shiftMovementDate = (days) => {
    const [year, month, day] = movementDate.split('-').map(Number);
    const date = new Date(year, month - 1, day + days);
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    setMovementDate(localDate.toISOString().slice(0, 10));
  };

  const formatMovementDate = (value) => new Date(`${value}T12:00:00`).toLocaleDateString(undefined, {
    weekday: 'long',
  });

  const getLocationLabel = (location) => {
    const found = DASHBOARD_LOCATIONS.find(({ key }) => key === String(location || '').toLowerCase());
    return found ? t(found.labelKey) : location || t('notAvailable');
  };

  const applyMovementSearch = (items) => {
    const query = searchQuery.trim().toLocaleLowerCase();
    if (!query) return items;
    return items.filter((item) => [
      item.productName,
      item.brandName,
      item.categoryName || getCategoryName(item),
      item.supplierName,
      item.location,
      getLocationLabel(item.location),
      item.notes,
      item.userId,
      item.quantityEntered,
      item.quantityExited,
      item.totalWeight,
    ].some((value) => String(value ?? '').toLocaleLowerCase().includes(query)));
  };

  const sortMovementsByNewest = (items) => [...items].sort((a, b) => {
    const aTime = new Date(a.enteredAt || a.exitedAt || 0).getTime();
    const bTime = new Date(b.enteredAt || b.exitedAt || 0).getTime();
    return (Number.isNaN(bTime) ? 0 : bTime) - (Number.isNaN(aTime) ? 0 : aTime);
  });

  // Fetch dashboard data on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Load brands and inventory summaries
        const [brandRes, res, coldStorage3Res, categoryRes] = await Promise.all([
          api.get('/brands'),
          api.get('/inventory/summary'),
          api.get('/inventory/summary?location=coldstorage3'),
          api.get('/categories').catch((error) => {
            console.error('Failed to load dashboard categories', error);
            return { data: [] };
          }),
        ]);
        setBrands(Array.isArray(brandRes.data) ? brandRes.data : []);
        setCategories(Array.isArray(categoryRes.data) ? categoryRes.data : []);

        // Validate response structure
        if (!res.data || typeof res.data !== 'object') {
          console.warn('Invalid inventory summary response:', res.data);
          setData({});
          return;
        }

        // Format the response into our expected structure
        const formatted = {};
        for (const [loc, summary] of Object.entries(res.data)) {
          formatted[loc] = {
            totalBoxes: summary?.totals?.totalBoxes ?? 0,
            totalWeight: summary?.totals?.totalWeight ?? 0,
            summaries: Array.isArray(summary?.products)
              ? summary.products
              : [],
          };
        }

        const coldStorage3Summary = coldStorage3Res.data?.coldstorage3;
        if (coldStorage3Summary) {
          formatted.coldstorage3 = {
            totalBoxes: coldStorage3Summary?.totals?.totalBoxes ?? 0,
            totalWeight: coldStorage3Summary?.totals?.totalWeight ?? 0,
            summaries: Array.isArray(coldStorage3Summary?.products)
              ? coldStorage3Summary.products
              : [],
          };
        }

        // Ensure all sections exist even if backend omits them
        DASHBOARD_LOCATIONS.forEach(({ key }) => {
          if (!formatted[key]) {
            formatted[key] = {
              totalBoxes: 0,
              totalWeight: 0,
              summaries: [],
            };
          }
        });

        setData(formatted);

        // Initialize collapsible sections after ensuring all keys exist
        const initState = {};
        Object.keys(formatted).forEach((loc) => {
          initState[loc] = false;
        });
        setOpenSections(initState);
      } catch (err) {
        console.error('Failed to load dashboard data', err);
        // If error, fallback to empty data to allow UI to render
        setData({});
      } finally {
        // Ensure loading is stopped even on error
        setTimeout(() => setLoading(false), 700);
      }
    };
    fetchData();
  }, []);

  // Toggle collapsible section state
  const toggleSection = (key) => {
    setOpenSections((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // Get brand name by id
  const getBrandName = (brandId) => {
    const brand = brands.find((b) => b.id === brandId || b._id === brandId);
    return brand ? brand.name : t('notAvailable');
  };

  const getCategoryName = (item) => item.categoryName || categories.find((category) =>
    String(category.id || category._id || '') === String(item.categoryId || '')
  )?.name || t('notAvailable');

  // Apply sorting
  const applySorting = (summaries) => {
    let sorted = [...summaries];
    switch (sortOption) {
      case 'boxesDesc':
        sorted.sort((a, b) => b.totalBoxes - a.totalBoxes);
        break;
      case 'productNameAsc':
        sorted.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'brandAsc':
        sorted.sort((a, b) =>
          getBrandName(a.brandId).localeCompare(getBrandName(b.brandId))
        );
        break;
      default:
        break;
    }
    return sorted;
  };

  // Apply search filter
  const applySearch = (summaries) => {
    const query = searchQuery.trim().toLocaleLowerCase();
    if (!query) return summaries;
    return summaries.filter(
      (item) =>
        String(item.name || '').toLocaleLowerCase().includes(query) ||
        String(getBrandName(item.brandId) || '').toLocaleLowerCase().includes(query) ||
        String(getCategoryName(item) || '').toLocaleLowerCase().includes(query) ||
        String(item.supplierName || '').toLocaleLowerCase().includes(query)
    );
  };

  // Open sections with matches while searching so filtered products are visible.
  useEffect(() => {
    if (!searchQuery.trim()) return;

    const nextOpenSections = {};
    DASHBOARD_LOCATIONS.forEach(({ key }) => {
      nextOpenSections[key] = applySearch(data[key]?.summaries || []).length > 0;
    });
    setOpenSections((previous) => ({ ...previous, ...nextOpenSections }));
  }, [searchQuery, data, brands, categories]);

  // Render helper: creates a section with header, optional totals and product cards
  const renderSection = (titleKey, key, showTotals = false, customTotalLabel = null) => {
    if (locationFilter !== 'all' && locationFilter !== key) return null;

    const section = data[key];
    if (!section) return null;

    const filtered = applySearch(section.summaries);
    const sortedSummaries = applySorting(filtered);
    const visibleTotals = filtered.reduce(
      (totals, item) => ({
        totalBoxes: totals.totalBoxes + (Number(item.totalBoxes) || 0),
        totalWeight: totals.totalWeight + (Number(item.totalWeight) || 0),
      }),
      { totalBoxes: 0, totalWeight: 0 }
    );

    return (
      <div className="border rounded-xl overflow-hidden shadow-md bg-gray-100 dark:bg-gray-700">
        {/* Section header with collapse toggle */}
        <button
          onClick={() => toggleSection(key)}
          className="w-full flex items-center justify-between px-6 py-4"
        >
          <span className="mx-auto text-lg font-bold text-gray-900 dark:text-white uppercase tracking-wide">
            {t(`dashboard.${titleKey}.title`, titleKey)}
          </span>
          {openSections[key] ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
        </button>

        {/* Collapsible content */}
        {openSections[key] && (
          <div className="p-6 space-y-6 bg-white dark:bg-gray-800">
            {/* Totals (optional) */}
            {showTotals && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white dark:bg-gray-900 p-6 rounded-xl shadow-md">
                  <h2 className="text-xl font-bold mb-2 flex items-center gap-2">
                    {customTotalLabel ? (
                      <>
                        <Package size={22} /> {customTotalLabel}
                      </>
                    ) : (
                      <>
                        <Boxes size={22} /> {t(`dashboard.${key}.totalBoxes`)}
                      </>
                    )}
                  </h2>
                  <p className="text-3xl font-extrabold">{visibleTotals.totalBoxes}</p>
                </div>

                <div className="bg-white dark:bg-gray-900 p-6 rounded-xl shadow-md">
                  <h2 className="text-xl font-bold mb-2 flex items-center gap-2">
                    <Weight size={22} /> {t(`dashboard.${key}.totalWeight`)}
                  </h2>
                  <p className="text-3xl font-extrabold">
                    {visibleTotals.totalWeight.toFixed(2)} {t('kg')}
                  </p>
                </div>
              </div>
            )}

            {/* Inventory product cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              {sortedSummaries.map((item) => (
                <div
                  key={item.productId}
                  className="bg-white dark:bg-gray-900 p-4 rounded-lg shadow"
                >
                  <h3 className="text-md font-bold text-gray-900 dark:text-white mb-2">
                    {item.name}
                  </h3>
                  <p className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                    <Tag size={16} /> {getBrandName(item.brandId)}
                  </p>
                  <p className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                    <ClipboardList size={16} /> {getCategoryName(item)}
                  </p>
                  <p className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                    <Factory size={16} /> {item.supplierName || t('notAvailable')}
                  </p>
                  <p className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                    <Boxes size={16} /> {item.totalBoxes}
                    <InventoryStockLed location={key} quantity={item.totalBoxes} />
                  </p>
                  <p className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                    <Weight size={16} /> {item.totalWeight.toFixed(2)} {t('kg')}
                  </p>
                </div>
              ))}
              {sortedSummaries.length === 0 && (
                <p className="text-center text-gray-500 dark:text-gray-400">
                  {t('noResults')}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderMovementCards = (title, items, kind, scrollRef) => {
    const scrollCards = (direction) => {
      if (scrollRef.current) scrollRef.current.scrollBy({ left: direction * 320, behavior: 'smooth' });
    };

    return (
    <section className="min-w-0 space-y-3 text-center">
      <div className="flex items-center justify-center gap-3">
        {items.length > 1 && <button type="button" onClick={() => scrollCards(-1)} aria-label={t('scrollLeft')} className="rounded-md border border-gray-300 bg-white p-1.5 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700"><ChevronLeft size={16} /></button>}
        <h3 className="text-lg font-bold text-gray-900 dark:text-white">{title}</h3>
        {items.length > 1 && <button type="button" onClick={() => scrollCards(1)} aria-label={t('scrollRight')} className="rounded-md border border-gray-300 bg-white p-1.5 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700"><ChevronRightIcon size={16} /></button>}
      </div>
      {movementsLoading ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">{t('loading')}</p>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white/90 px-5 py-6 text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-800/90 dark:text-gray-400">
          {t('noResults')}
        </div>
      ) : (
        <div ref={scrollRef} className="movement-cards-scroll flex w-full min-w-0 gap-4 overflow-x-auto overscroll-x-contain pb-4 snap-x snap-mandatory">
          {items.map((item, index) => {
            const timestamp = item.enteredAt || item.exitedAt;
            return (
              <article
                key={item.id || item._id || `${kind}-${index}`}
                className="w-[280px] min-w-[280px] flex-none snap-start rounded-lg bg-white p-4 text-left shadow dark:bg-gray-900"
              >
                <h4 className="mb-2 truncate text-md font-bold text-gray-900 dark:text-white">{item.productName || t('notAvailable')}</h4>
                <p className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200"><Tag size={16} /> {item.brandName || t('notAvailable')}</p>
                <p className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200"><ClipboardList size={16} /> {item.categoryName || getCategoryName(item)}</p>
                <p className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200"><Factory size={16} /> {item.supplierName || t('notAvailable')}</p>
                <p className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200"><Boxes size={16} /> {kind === 'entry' ? item.quantityEntered : item.quantityExited} {t('boxes')}</p>
                <p className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200"><Weight size={16} /> {(Number(item.totalWeight) || 0).toFixed(2)} {t('kg')}</p>
                <p className="mt-1 text-sm font-medium text-gray-600 dark:text-gray-300">{getLocationLabel(item.location)}</p>
                {kind === 'exit' && <p className="mt-2 text-sm font-medium text-gray-700 dark:text-gray-200"><span className="font-semibold">{t('exitClient')}:</span> <span className="italic">{item.clientName || t('noClient')}</span></p>}
                {item.notes?.trim() && <p className="mt-2 text-sm font-medium text-gray-700 dark:text-gray-200"><span className="font-semibold">{t('notes')}:</span> <span className="italic">{item.notes}</span></p>}
                {timestamp && <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>}
                {item.userId && <p className="mt-1 text-xs font-medium text-gray-600 dark:text-gray-300"><span>{t('createdBy')}:</span> {item.userId}</p>}
              </article>
            );
          })}
        </div>
      )}
    </section>
    );
  };

  // Loading screen animation
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen overflow-x-hidden px-4 space-y-4">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 1.5, ease: 'linear' }}
        >
          <Boxes size={64} className="text-black dark:text-white" />
        </motion.div>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, repeat: Infinity, repeatType: 'reverse' }}
          className="text-lg text-gray-700 dark:text-gray-300 font-semibold text-center"
        >
          {t('loadingDashboard')}
        </motion.p>
      </div>
    );
  }

  // Render dashboard with all inventory sections
  return (
    <div className="max-w-6xl mx-auto space-y-8 py-6 px-4 sm:px-6 md:px-8 overflow-x-hidden">
      <PageHeading icon={<LayoutDashboard size={26} />} title={t('dashboardTitle')} className="mb-2" />
      {/* Controls: search + sort */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6 w-full">
        {/* Search bar */}
        <div className="relative w-full">
          <Search
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            placeholder={t('searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label={t('searchPlaceholder')}
            className="h-11 w-full pl-10 pr-4 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-blue-500 focus:outline-none"
          />
        </div>

        {/* Sort dropdown */}
        <select
          value={sortOption}
          onChange={(e) => setSortOption(e.target.value)}
          aria-label={t('sortResults')}
          className="h-11 w-full px-4 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-blue-500 focus:outline-none"
        >
          <option value="boxesDesc">{t('sortMostBoxes')}</option>
          <option value="productNameAsc">{t('sortProductNameAsc')}</option>
          <option value="brandAsc">{t('sortBrandAsc')}</option>
        </select>

        <select
          value={locationFilter}
          onChange={(e) => setLocationFilter(e.target.value)}
          aria-label={t('locationFilter')}
          className="h-11 w-full px-4 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-blue-500 focus:outline-none"
        >
          <option value="all">{t('locationFilter')}</option>
          {DASHBOARD_LOCATIONS.map(({ key, labelKey }) => (
            <option key={key} value={key}>{t(labelKey)}</option>
          ))}
        </select>
      </div>

      <section className="mb-8 overflow-hidden rounded-xl border bg-gray-100 shadow-md dark:bg-gray-700">
        <button
          type="button"
          onClick={() => toggleSection('dailyMovements')}
          aria-expanded={Boolean(openSections.dailyMovements)}
          className="flex w-full items-center justify-between px-6 py-4"
        >
          <span className="mx-auto text-lg font-bold uppercase tracking-wide text-gray-900 dark:text-white">{t('dailyMovements')}</span>
          {openSections.dailyMovements ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
        </button>
        {openSections.dailyMovements && <div className="space-y-6 bg-white p-4 text-center dark:bg-gray-800 sm:p-6">
        <div className="flex flex-col items-center gap-3">
          <p className="text-xl font-bold capitalize text-gray-900 dark:text-white">{formatMovementDate(movementDate)}</p>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => shiftMovementDate(-1)} aria-label={t('previousDay')} className="rounded-lg border border-gray-300 bg-white p-2 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700"><ChevronLeft size={18} /></button>
            <input type="date" value={movementDate} onChange={(event) => setMovementDate(event.target.value)} aria-label={t('selectMovementDate')} className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100" />
            <button type="button" onClick={() => shiftMovementDate(1)} aria-label={t('nextDay')} className="rounded-lg border border-gray-300 bg-white p-2 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700"><ChevronRightIcon size={18} /></button>
          </div>
        </div>
        {renderMovementCards(t('entries'), applyMovementSearch(sortMovementsByNewest(dailyEntries)), 'entry', entriesScrollRef)}
        {renderMovementCards(t('exits'), applyMovementSearch(sortMovementsByNewest(dailyExits)), 'exit', exitsScrollRef)}
        </div>}
      </section>

      {/* Sections */}
      {renderSection('coldstorage1', 'coldstorage1', true, t('dashboard.coldstorage1.totalMerchandise'))}
      {renderSection('coldstorage2', 'coldstorage2', true)}
      {renderSection('coldstorage3', 'coldstorage3', true)}
      {renderSection('refrigerators', 'refrigerators', false)}
      {renderSection('totems', 'totems', false)}
      {renderSection('warehouse', 'warehouse', false)}
    </div>
  );
};

export default Dashboard;
